import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { sendGet } from 'hooks/use-fetch'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import Skeleton from '@mui/material/Skeleton'
import { BirthdayToAgeInYearsAndMonths } from 'core/Utilities'
import DateRangeIcon from '@mui/icons-material/DateRange'
import Button from '@mui/material/Button'
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined'
import S3Avatar from 'Components/S3Image/S3Avatar'
import Avatar from '@mui/material/Avatar'
import NotesIcon from '@mui/icons-material/Notes'
import { ActionMenu } from 'Components/Menu/ActionMenu'
import { ActionItem } from 'Components/Menu/ActionItem'

interface Props {
  siblingId: number
  childId: number
}

interface SiblingDetailsModel {
  firstName: string
  lastName: string
  dateOfBirth: Date
  isUnborn: boolean
  address: string
  city: string
  state: string
  zip: string
  race: string
  tribalAffiliationStatus: boolean
  tribalAffiliationName: string | null
  tribalAffiliationID: number | null
  primaryLanguage: string
  s3Key: string | null
}

const SiblingDetails = (props: Props): JSX.Element => {
  const nav = useNavigate()
  const [sibling, setSibling] = useState<SiblingDetailsModel | null>(null)
  const [showMenu, setShowMenu] = useState(false)

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      const { response, success } = await sendGet(
        `/SiblingDetails/GetDetails?childId=${props.childId}&siblingId=${props.siblingId}`, {})

      success && setSibling(response.response)
    }

    void fetchData()
  }, [props.siblingId])

  const onHide = (): void => {
    setShowMenu(false)
  }
  const onShow = (): void => {
    setShowMenu(true)
  }
  const goToDetails = (): void => {
    nav(`/CareNetworkManagement/Details/${props.siblingId}`)
  }

  if (sibling == null) return <Skeleton />
  return <Box className='CardDisplay'>
    <Typography variant='h6' sx={{ fontWeight: 'bold', display: 'inline-block' }} gutterBottom>Sibling Details</Typography>
    <Box sx={{ float: 'right' }}>
      <ActionMenu open={showMenu} onClose={onHide} onShow={onShow} testId='siblingMenu' menuProps={{ border: 'none' }}>
        <Box>
          <ActionItem testId='goToDetails' onClick={goToDetails} >Go to Child Details</ActionItem>
        </Box>
      </ActionMenu>
    </Box>

    <Stack direction='column' spacing={1}>
      <Stack direction='row' spacing={1}>
        {sibling.s3Key !== null
          ? <S3Avatar imageKey={sibling.s3Key} height='30px' width='30px' />
          : <Avatar sx={{ height: '30px', width: '30px' }} />}
        <Typography variant='body1' sx={{ fontWeight: 'bold', overflowWrap: 'break-word' }}>
          {`${sibling.isUnborn ? '(unborn)' : sibling.firstName} ${sibling.lastName}`}
        </Typography>
      </Stack>
      <Stack direction='row' spacing={1}>
        <DateRangeIcon />
        <Stack direction='row' spacing={1} flexWrap='wrap' alignItems='center'>
          <Typography variant='body1' sx={{ overflowWrap: 'break-word' }}>
            {sibling.isUnborn
              ? '(unborn)'
              : (sibling?.dateOfBirth !== null && sibling?.dateOfBirth != null && new Date(sibling?.dateOfBirth).toLocaleString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }))}
          </Typography>
          {sibling !== null && !sibling.isUnborn && <Typography variant='body2' sx={{ overflowWrap: 'break-word' }}>
            ({BirthdayToAgeInYearsAndMonths(sibling.dateOfBirth, false)})
          </Typography>}
        </Stack>
      </Stack>
      <Stack direction='row' spacing={1}>
        <PlaceOutlinedIcon />
        <Stack direction='column'>
          <Typography variant='body1' sx={{ overflowWrap: 'break-word' }}>
            {sibling.address}
          </Typography>
          <Typography variant='body1' sx={{ overflowWrap: 'break-word' }}>
            {`${sibling.city}, ${sibling.state}`}
          </Typography>
          <Typography variant='body1' sx={{ overflowWrap: 'break-word' }}>
            {sibling.zip}
          </Typography>
        </Stack>
      </Stack>
      {!sibling.isUnborn && <Stack direction='row' spacing={1}>
        <NotesIcon />
        <Stack direction='column'>
          <Typography variant='body1' sx={{ overflowWrap: 'break-word' }}>
            {sibling.race}
          </Typography>
          {sibling.tribalAffiliationStatus && (
            <Typography variant='body1' sx={{ overflowWrap: 'break-word' }}>
              {sibling.tribalAffiliationName}
            </Typography>

          )}
          <Typography variant='body1' sx={{ overflowWrap: 'break-word' }}>
            {sibling.primaryLanguage}
          </Typography>
        </Stack>
      </Stack>
      }
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <Button
          variant='contained'
          onClick={() => {
            nav(`/SiblingDetails/Edit/${props.siblingId}/Child/${props.childId}`)
          }}
          sx={{ textTransform: 'none', px: 4 }}
          data-testid='editSiblingButton'
        >
          Edit Sibling
        </Button>
      </Box>
    </Stack>
  </Box>
}

export default SiblingDetails
